import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Button from 'components/button';
import UserInput from 'components/user-input';
import PhoneInput from 'components/phone-input';
import IconButton from 'components/icon-button';
import BottomSheet from 'components/bottom-sheet';
import BottomSheetItem from 'components/bottom-sheet-item';
import BottomSheetItemsContainer from 'components/bottom-sheet-items-container';

import useBoundStore from 'store';
import { BuyApartmentFlowPaths, CommonPaths, NotificationFlowPaths, TemporaryPaths } from 'routes';
import { SellApartmentFlowPaths } from 'routes';
import { createCallRequest, logOut, requestSyncCalendarLinkToChat } from 'api';
import list from 'assets/images/list.svg';
import caretLeft from 'assets/images/caret-left.svg';
import styles from './styles.module.scss';
import BottomSheetItemFeedBack from 'components/bottom-sheet-item/bottomSheetItemFeedback';
import BottomSheetConfirm from 'components/bottom-sheet-confirm';
import toasts from 'components/toasts';
import { copyToClipboard } from 'lib/utils';

const enum MenuView {
  main,
  profile,
  calendarSync,
}

const Menu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useBoundStore((s) => s.user);

  const isClient = useMemo(() => user?.type === 'Client', [user]);
  const isRealtor = useMemo(() => user?.type === 'Realtor', [user]);

  const showToast = useBoundStore((s) => s.showToast);
  const openAuthBottomSheet = useBoundStore((s) => s.openAuthBottomSheet);
  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);

  const [view, setView] = useState(MenuView.main);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateCallLoading, setIsCreateCallLoading] = useState(false);

  const handleProfile = () => {
    if (user) {
      setView(MenuView.profile);
    } else {
      openAuthBottomSheet();
    }
  };

  const handleBuyMode = () => {
    if (location.pathname === BuyApartmentFlowPaths.chooseMode) {
      setIsOpen(false);
    } else {
      navigate(BuyApartmentFlowPaths.chooseMode);
    }
  };

  const handleSellApartmentClick = () => {
    if (location.pathname === SellApartmentFlowPaths.mainSellChooseMode) {
      setIsOpen(false);
    } else {
      navigate(SellApartmentFlowPaths.mainSellChooseMode);
    }
  };

  const handleNotificationsClick = () => {
    if (location.pathname === NotificationFlowPaths.main) {
      setIsOpen(false);
    } else {
      navigate(NotificationFlowPaths.main);
    }
  };

  const handleTemporaryClick = () => {
    if (location.pathname === TemporaryPaths.main) {
      setIsOpen(false);
    } else {
      navigate(TemporaryPaths.main);
    }
  };

  const handleContactsClick = () => {
    if (location.pathname === CommonPaths.contacts) {
      setIsOpen(false);
    } else {
      navigate(CommonPaths.contacts);
    }
  };

  const handleFavoriteEstateObjectsClick = () => {
    if (location.pathname === BuyApartmentFlowPaths.apartmentFavorites) {
      setIsOpen(false);
    } else {
      navigate(BuyApartmentFlowPaths.apartmentFavorites);
    }
  };

  const handleDealClick = () => {
    if (forceAuthorize())
      if (location.pathname === BuyApartmentFlowPaths.requests) {
        setIsOpen(false);
      } else {
        navigate(BuyApartmentFlowPaths.requests);
      }
  };

  const handleCalendarSync = () => {
    if (forceAuthorize()) {
      setView(MenuView.calendarSync);
    }
  };

  const handleEstimateClick = () => {
    navigate(CommonPaths.estimate);
  };

  const handleContact = async () => {
    setIsCreateCallLoading(true);
    const { result } = await createCallRequest();
    setIsCreateCallLoading(false);

    if (result) {
      handleDismissBottomSheet();
      showToast({ message: 'Свяжемся в течение 15 минут' });
    }
  };

  const handleDismissBottomSheet = () => {
    setIsOpen(false);
    setView(MenuView.main);
  };

  const header =
    view === MenuView.main ? undefined : (
      <div className={styles.profileHeader}>
        <div>Профиль</div>
        <IconButton
          className={styles.profileBackButton}
          icon={caretLeft}
          onClick={() => setView(MenuView.main)}
        />
      </div>
    );

  return (
    <>
      <IconButton icon={list} alt='Меню' onClick={() => setIsOpen(true)} />
      <BottomSheet
        open={isOpen}
        header={header}
        onDismiss={handleDismissBottomSheet}
        snapPoints={({ minHeight, maxHeight }) => [minHeight, maxHeight]}
        noPadding={view === MenuView.profile}
        className={styles.bottomSheet}
        blocking={false}
      >
        {view === MenuView.main ? (
          <>
            <BottomSheetItemsContainer>
              <BottomSheetItem title='Профиль/Авторизация' onClick={handleProfile} />
              {isRealtor || isClient ? (
                <>
                  <BottomSheetItem
                    title={isRealtor ? 'Мои контакты' : 'Мои риелторы'}
                    onClick={handleContactsClick}
                  />
                </>
              ) : (
                <></>
              )}

              {!isClient ? (
                <>
                  <BottomSheetItem title='Мои объекты' onClick={handleSellApartmentClick} />
                  <BottomSheetItem title='Поиск' onClick={handleBuyMode} />
                </>
              ) : (
                <></>
              )}
              <BottomSheetItem title='Оценка' onClick={handleEstimateClick} />
              <BottomSheetItem
                title='Избранное'
                onClick={handleFavoriteEstateObjectsClick}
              />
              {!isClient ? (
                <>
                  <BottomSheetItem title='Временные' onClick={handleTemporaryClick} />
                </>
              ) : (
                <></>
              )}
              <BottomSheetItem title='Уведомления' onClick={handleNotificationsClick} />
              {!isClient && !isRealtor && (
                <>
                  <BottomSheetItem title='Мои запросы' onClick={handleDealClick} />
                  <BottomSheetItem title='Мои события' onClick={handleCalendarSync} />
                </>
              )}
              <BottomSheetItemFeedBack
                title='Сообщить об ошибке'
                onClick={() => setIsOpen(false)}
              />
            </BottomSheetItemsContainer>

            <Button
              variant='gray'
              label='Связаться с сотрудником Reit'
              onClick={handleContact}
              className={styles.contactButton}
              loading={isCreateCallLoading}
            />
          </>
        ) :
          view === MenuView.profile ? (
          <UserProfileTab />
          ) :
            view === MenuView.calendarSync ? (
              <CalendarSyncTab />
            )
              : <></>
        }
      </BottomSheet>
    </>
  );
};

const UserProfileTab: React.FC = () => {
  const user = useBoundStore((s) => s.user);
  const clearUser = useBoundStore((s) => s.clearUser);
  const navigate = useNavigate();
  const [logOutModal, setLogOutModal] = useState(false);
  const handleLogOut = async () => {
    await logOut();
    navigate('/');
    clearUser();
  };

  return (
    <div className={styles.userProfileContainer}>
      <UserInput readOnly value={user?.firstName} />
      <PhoneInput readOnly placeholder={user?.phone} />
      <Button label='Выйти' variant='danger' onClick={() => setLogOutModal(true)} />
      {/* <EmailInput readOnly value='foo@bar.baz' /> */}
      <BottomSheetConfirm
        isOpen={logOutModal}
        onAgree={handleLogOut}
        nameAction='Выйти'
        title={'Вы уверены, что хотите выйти?'}
        onDismiss={() => setLogOutModal(false)}
        isLoading={false}
      />
    </div>
  );
};

const CalendarSyncTab: React.FC = () => {

  const [syncLink, setSyncLink] = useState<{
    webcal: string;
    https: string;
  } | null>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const getSyncLink = useCallback(async () => {
    setIsLoading(true)
    try {
      const req = await requestSyncCalendarLinkToChat()
      if (req.result) {
        setSyncLink(req.result)
      }
      else {
        throw new Error('Нет данных запроса')
      }
    }
    catch {
      setSyncLink(null)
      toasts.error('Ошибка генерации ссылки')
    }
    finally {
      setIsLoading(false)
    }
  }, [])

  return (
    <div className={styles.userProfileContainer}>
      <>
        <Button label='Синхронизировать' variant='secondary' onClick={getSyncLink} loading={isLoading} />
        {syncLink && (
          <>
            <h2>Для IOS</h2>
            <div>
              скопируйте ссылку и вставьте в Safary
            </div>
            <br />
            <i style={{ fontWeight: 600, color: '#009d68', textDecoration: 'underline' }} onClick={() => { copyToClipboard(syncLink.webcal, 'Скопирована ссылка для IOS') }}>Ссылка для IOS</i>
            <h2>Для Google Calendar</h2>
            <div>
              <p>
                перейдите на форму добавления календаря
              </p>
              <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl">
                https://calendar.google.com/calendar/u/0/r/settings/addbyurl
              </a>
              <p>
                вставьте ссылку и нажмите "Добавить"
              </p>
            </div>
            <br />
            <i style={{ fontWeight: 600, color: '#009d68', textDecoration: 'underline' }} onClick={() => { copyToClipboard(syncLink.https, 'Скопирована ссылка для Google Calendar') }}>Ссылка для Google Calendar</i>
          </>
        )}
      </>
    </div>
  );
};

export default Menu;
